// src/components/dashboard/tabs/DashboardGroupsTab.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Typography,
    TextField,
    InputAdornment,
    CircularProgress,
    Alert,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    OutlinedInput,
    FormHelperText
} from '@mui/material';
import GradientButton from '../../ui/GradientButton';
import {
    Users,
    Search,
    Plus,
    Edit,
    Trash2,
    UserPlus
} from 'lucide-react';
import {
    fetchUserGroups,
    createGroup,
    updateGroup,
    deleteGroup
} from '../../../services/api';
import GroupMembersDialog from './GroupMembersDialog';
import GroupDetailDialog from './GroupDetailDialog';

// Group Card Component
const GroupCard = ({ group, onEdit, onDelete, onShare, onViewDetail, token, setError, userInfo }) => {
    const { t } = useTranslation();

    const handleEdit = () => {
        onEdit(group);
    };

    const handleDelete = async () => {
        if (window.confirm(t('dashboard.groups.confirm_delete'))) {
            try {
                await deleteGroup(token, group.id);
                onDelete(group.id);
            } catch (error) {
                setError(t('dashboard.groups.error.deleting'));
            }
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div 
                className="p-4 cursor-pointer"
                onClick={() => onViewDetail(group)}
            >
                <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center">
                        {group.avatar ? (
                            <img 
                                src={group.avatar} 
                                alt={group.name} 
                                className="w-10 h-10 rounded-full mr-3 object-cover"
                            />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                <Users className="w-5 h-5 text-blue-500" />
                            </div>
                        )}
                        <div>
                            <h3 className="font-medium text-gray-900">{group.name}</h3>
                            <p className="text-xs text-gray-500">
                                {t('dashboard.groups.members_count', { count: group.members?.length || 0 })}
                            </p>
                        </div>
                    </div>
                </div>

                {group.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{group.description}</p>
                )}

                <div className="mt-4">
                    <Typography variant="caption" className="text-gray-500 block mb-2">
                        {t('dashboard.groups.members')}:
                    </Typography>
                    <div className="flex flex-wrap gap-1">
                        {group.members?.slice(0, 3).map(member => (
                            <Chip 
                                key={member.id} 
                                label={member.username} 
                                size="small" 
                                className="bg-gray-100"
                            />
                        ))}
                        {group.members?.length > 3 && (
                            <Chip 
                                label={`+${group.members.length - 3} more`} 
                                size="small" 
                                className="bg-blue-100 text-blue-700 cursor-pointer hover:bg-blue-200"
                                onClick={(e) => {
                                    e.stopPropagation(); // Предотвращаем срабатывание клика по карточке
                                    onViewDetail(group);
                                }}
                            />
                        )}
                        {(!group.members || group.members.length === 0) && (
                            <Typography variant="caption" className="text-gray-400 italic">
                                {t('dashboard.groups.no_members')}
                            </Typography>
                        )}
                    </div>
                </div>
            </div>

            {/* Кнопки управления вне кликабельной области */}
            <div className="px-4 pb-4 flex justify-end space-x-2">
                {/* Показываем кнопки редактирования только для владельца */}
                {group.owner === userInfo.id && (
                    <>
                        <button 
                            onClick={handleEdit}
                            className="p-1 text-gray-500 hover:text-blue-500 transition-colors"
                            title={t('dashboard.groups.edit')}
                        >
                            <Edit className="w-4 h-4" />
                        </button>
                        <button 
                            onClick={handleDelete}
                            className="p-1 text-gray-500 hover:text-red-500 transition-colors"
                            title={t('dashboard.groups.delete')}
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </>
                )}
                {/* Кнопку управления участниками показываем только владельцу */}
                {group.owner === userInfo.id && (
                    <button 
                        onClick={() => onShare(group)}
                        className="p-1 text-gray-500 hover:text-purple-500 transition-colors"
                        title={t('dashboard.groups.manage_members')}
                    >
                        <UserPlus className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
};

// Group Dialog Component
const GroupDialog = ({ open, onClose, onSubmit, group, error, setError }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [nameError, setNameError] = useState('');

    useEffect(() => {
        if (group) {
            setName(group.name || '');
            setDescription(group.description || '');
        } else {
            setName('');
            setDescription('');
        }
        setNameError('');
    }, [group, open]);

    const handleSubmit = () => {
        if (!name.trim()) {
            setNameError(t('dashboard.groups.error.name_required'));
            return;
        }

        onSubmit({
            name,
            description
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {group ? t('dashboard.groups.edit_group') : t('dashboard.groups.create_group')}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" className="mb-4" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <FormControl fullWidth margin="normal" error={!!nameError}>
                    <InputLabel htmlFor="group-name">{t('dashboard.groups.name')}</InputLabel>
                    <OutlinedInput
                        id="group-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        label={t('dashboard.groups.name')}
                    />
                    {nameError && <FormHelperText>{nameError}</FormHelperText>}
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel htmlFor="group-description">{t('dashboard.groups.description')}</InputLabel>
                    <OutlinedInput
                        id="group-description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        label={t('dashboard.groups.description')}
                        multiline
                        rows={3}
                    />
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    color="primary"
                >
                    {group ? t('common.save') : t('common.create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Main Component
const DashboardGroupsTab = ({ userInfo, token, setError }) => {
    const { t } = useTranslation();

    // States for data
    const [groups, setGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dialogError, setDialogError] = useState(null);

    // States for search
    const [searchTerm, setSearchTerm] = useState('');

    // States for dialogs
    const [groupDialogOpen, setGroupDialogOpen] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedGroupForDetail, setSelectedGroupForDetail] = useState(null);

    // Load data
    useEffect(() => {
        loadGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, userInfo]);

    const loadGroups = async () => {
        setIsLoading(true);
        try {
            const response = await fetchUserGroups(token);
            const groupsData = Array.isArray(response) ? response : [];
            setGroups(groupsData);
        } catch (err) {
            setError(t('dashboard.groups.error.loading'));
            
        } finally {
            setIsLoading(false);
        }
    };

    // Get filtered and categorized groups
    const getFilteredGroups = () => {
        let allGroups = [...groups];

        // Filter by search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            allGroups = allGroups.filter(group => 
                group.name.toLowerCase().includes(term) || 
                (group.description && group.description.toLowerCase().includes(term))
            );
        }

        // Separate groups into administered and participated
        const administeredGroups = allGroups.filter(group =>
            group.owner === userInfo.id
        );

        const participatedGroups = allGroups.filter(group =>
            group.owner !== userInfo.id
        );

        return {
            administeredGroups,
            participatedGroups
        };
    };

    // Handle group creation/update
    const handleSubmitGroup = async (data) => {
        try {
            setDialogError(null);

            if (selectedGroup) {
                // Update existing group
                await updateGroup(token, selectedGroup.id, data);

                // Update local state
                setGroups(prevGroups => 
                    prevGroups.map(group => 
                        group.id === selectedGroup.id 
                            ? { ...group, ...data } 
                            : group
                    )
                );
            } else {
                // Create new group
                const newGroup = await createGroup(token, data);

                // Add to local state
                setGroups(prevGroups => [...prevGroups, newGroup]);
            }

            setGroupDialogOpen(false);
            setSelectedGroup(null);
        } catch (err) {
            setDialogError(
                selectedGroup 
                    ? t('dashboard.groups.error.updating') 
                    : t('dashboard.groups.error.creating')
            );
            
        }
    };

    // Handle group deletion
    const handleDeleteGroup = (groupId) => {
        setGroups(prevGroups => prevGroups.filter(group => group.id !== groupId));
    };

    // Handle opening edit dialog
    const handleEditGroup = (group) => {
        setSelectedGroup(group);
        setGroupDialogOpen(true);
    };

    // Handle opening share dialog
    const handleShareGroup = (group) => {
        setSelectedGroup(group);
        setShareDialogOpen(true);
    };

    // Handle opening detail dialog
    const handleViewGroupDetail = (group) => {
        setSelectedGroupForDetail(group);
        setDetailDialogOpen(true);
    };

    // Get filtered and categorized groups
    const { administeredGroups, participatedGroups } = getFilteredGroups();

    return (
        <div className="space-y-6">
            {/* Action panel with search and create button */}
            <div className="flex flex-col md:flex-row gap-4 items-center mb-6">
                <TextField
                    placeholder={t('dashboard.groups.search')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-grow"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search className="text-gray-400" />
                            </InputAdornment>
                        ),
                    }}
                    size="small"
                    fullWidth
                />

                <GradientButton
                    onClick={() => {
                        setSelectedGroup(null);
                        setGroupDialogOpen(true);
                    }}
                    startIcon={<Plus className="w-4 h-4" />}
                >
                    {t('dashboard.groups.create')}
                </GradientButton>
            </div>

            {/* Content */}
            <div className="space-y-6">
                {isLoading ? (
                    <div className="py-12 flex justify-center items-center bg-white rounded-lg shadow-sm">
                        <CircularProgress />
                        <Typography variant="body1" className="ml-4 text-gray-600">
                            {t('dashboard.loading')}
                        </Typography>
                    </div>
                ) : (administeredGroups.length === 0 && participatedGroups.length === 0) ? (
                    <Alert severity="info" className="my-6">
                        {t('dashboard.groups.no_groups')}
                    </Alert>
                ) : (
                    <>
                        {/* Administered Groups Section */}
                        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <UserPlus className="w-5 h-5 text-green-500" />
                                {t('dashboard.groups.filters.owned')}
                                <Chip
                                    label={administeredGroups.length}
                                    size="small"
                                    className="ml-2"
                                    sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
                                />
                            </Typography>

                            <div className="p-4">
                                {administeredGroups.length === 0 ? (
                                    <Alert severity="info">
                                        {t('dashboard.groups.no_owned_groups')}
                                    </Alert>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {administeredGroups.map(group => (
                                            <GroupCard
                                                key={group.id}
                                                group={group}
                                                onEdit={handleEditGroup}
                                                onDelete={handleDeleteGroup}
                                                onShare={handleShareGroup}
                                                onViewDetail={handleViewGroupDetail}
                                                token={token}
                                                setError={setError}
                                                userInfo={userInfo}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Participated Groups Section */}
                        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <Users className="w-5 h-5 text-purple-500" />
                                {t('dashboard.groups.filters.member')}
                                <Chip
                                    label={participatedGroups.length}
                                    size="small"
                                    className="ml-2"
                                    sx={{ backgroundColor: '#f3e5f5', color: '#7b1fa2' }}
                                />
                            </Typography>

                            <div className="p-4">
                                {participatedGroups.length === 0 ? (
                                    <Alert severity="info">
                                        {t('dashboard.groups.no_member_groups')}
                                    </Alert>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {participatedGroups.map(group => (
                                            <GroupCard
                                                key={group.id}
                                                group={group}
                                                onEdit={handleEditGroup}
                                                onDelete={handleDeleteGroup}
                                                onShare={handleShareGroup}
                                                onViewDetail={handleViewGroupDetail}
                                                token={token}
                                                setError={setError}
                                                userInfo={userInfo}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Dialogs */}
            <GroupDialog
                open={groupDialogOpen}
                onClose={() => {
                    setGroupDialogOpen(false);
                    setSelectedGroup(null);
                    setDialogError(null);
                }}
                onSubmit={handleSubmitGroup}
                group={selectedGroup}
                error={dialogError}
                setError={setDialogError}
            />

            {selectedGroup && (
                <GroupMembersDialog
                    open={shareDialogOpen}
                    onClose={() => {
                        setShareDialogOpen(false);
                        setSelectedGroup(null);
                    }}
                    group={selectedGroup}
                    token={token}
                    onError={setError}
                    onSuccess={() => {
                        setShareDialogOpen(false);
                        loadGroups(); // Refresh data after managing members
                    }}
                />
            )}

            {selectedGroupForDetail && (
                <GroupDetailDialog
                    open={detailDialogOpen}
                    onClose={() => {
                        setDetailDialogOpen(false);
                        setSelectedGroupForDetail(null);
                    }}
                    group={selectedGroupForDetail}
                    token={token}
                    userInfo={userInfo}
                />
            )}
        </div>
    );
};

export default DashboardGroupsTab;
